/* eslint-disable no-prototype-builtins */
import { TWEEN } from 'three/examples/jsm/libs/tween.module.min';
import { AnimationMixer, Quaternion, Vector3 } from 'three';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { findObject3DChildByName } from './utils';
import AbstractAvatar from './AbstractAvatar';
import {
  AVATAR_ANIMATION_BLENDING,
  AVATAR_ROTATION_SPEED,
  AVATAR_WALKING_SPEED,
} from '@/_models/virtualStaging/avatar';

export class AvatarComponent extends AbstractAvatar {
  private avatarRoot: any;

  private faceNode?: any; // nodes for current RPM version

  private teethNode?: any;

  private wolf3DAvatar?: any; // nodes for old RPM versions

  private mixer: AnimationMixer; // nodes for old RPM versions

  private animationActions = [];

  private activeAnimation = null;

  // instantly teleport avatar to position on first avatar_move event
  private avatarHasMoved = false;

  private vec3: Vector3;

  async onInit() {
    this.outputs.loadingState = LoadingState.Loading;

    const THREE = this.context.three;
    this.vec3 = new THREE.Vector3();
    this.initRoot();
    this.root.visible = false;

    if (
      !this.inputs.url ||
      this.inputs.url.includes('.png') ||
      this.inputs.url.includes('.jpg') ||
      this.inputs.url.includes('.jpeg')
    )
      this.inputs.bodyType = 'image';

    this.model = this.inputs.url
      ? await this.loadModel(THREE, this.inputs.url)
      : this.makeAvatarImageRound(this.inputs.label);

    switch (this.inputs.bodyType) {
      case 'fullbody':
        [this.avatarRoot] = this.model.children;
        this.faceNode = findObject3DChildByName(this.avatarRoot, 'Wolf3D_Head');
        this.teethNode = findObject3DChildByName(this.avatarRoot, 'Wolf3D_Teeth');
        this.wolf3DAvatar =
          findObject3DChildByName(this.avatarRoot, 'Wolf3D_Avatar') ||
          findObject3DChildByName(this.avatarRoot, 'Wolf3D_Body') ||
          findObject3DChildByName(this.avatarRoot, 'Hips');
        this.mixer = new THREE.AnimationMixer(this.wolf3DAvatar);
        this.root.parent.position.y = 0;
        this.pivot.rotation.set(-Math.PI / 2, 0, 0);
        this.model.scale.set(1.1, 1.1, 1.1);
        await this.initAnimations();
        break;
      case 'halfbody':
        [this.avatarRoot] = this.model.children;
        this.faceNode = findObject3DChildByName(this.avatarRoot, 'Wolf3D_Head');
        this.teethNode = findObject3DChildByName(this.avatarRoot, 'Wolf3D_Teeth');
        this.wolf3DAvatar = findObject3DChildByName(this.avatarRoot, 'Wolf3D_Avatar');
        this.mixer = new THREE.AnimationMixer(this.wolf3DAvatar);
        break;
      case 'image':
        this.model.scale.setScalar(0.4);
        this.model.position.y = 1.1;
        this.pivot.position.y = -1.2;
        break;
      default:
        break;
    }
    this.model.frustumCulled = false;
    if (this.model.children)
      this.model.traverse(function (object) {
        object.frustumCulled = false;
      });
    this.pivot.add(this.model);
    this.initLabel();

    this.outputs.loadingState = LoadingState.Loaded;
  }

  async initAnimations() {
    const THREE = this.context.three;
    const idleAnimation = await this.loadAnimation(THREE, '../../images/Idle.fbx');
    idleAnimation.animations[0].name = 'idle';
    const walkingAnimation = await this.loadAnimation(THREE, '../../images/Walking.fbx');
    walkingAnimation.animations[0].name = 'walking';

    const idleAnimationAction = this.mixer.clipAction(idleAnimation.animations[0]);
    const walkingAnimationAction = this.mixer.clipAction(walkingAnimation.animations[0]);
    this.animationActions.push(idleAnimationAction);
    this.animationActions.push(walkingAnimationAction);
    console.log(
      `Avatar: ${this.inputs.label}\n Animations loaded, initializing 'idle' state...`,
      this.animationActions,
      this,
    );
    this.fadeInAnimation('idle');
  }

  fadeInAnimation(clipName: 'idle' | 'walking') {
    const animation = this.animationActions.find((action) => action.getClip().name === clipName);
    if (animation) {
      if (animation === this.activeAnimation) {
        return;
      }
      if (this.activeAnimation) this.activeAnimation.fadeOut(AVATAR_ANIMATION_BLENDING);
      console.log(
        `Avatar: ${this.inputs.label}\n Animation ${animation._clip.name} start, last active animation is ${this.activeAnimation ? this.activeAnimation._clip.name : 'null'}`,
        animation,
      );
      animation.reset().fadeIn(AVATAR_ANIMATION_BLENDING).play();
      this.activeAnimation = animation;
    }
  }

  moveToPos(
    target: Vector3,
    qt: Quaternion,
    onStart?: (arg: TWEEN.Tween) => void,
    onComplete?: (arg: TWEEN.Tween) => void,
  ) {
    const distance = this.root.parent.position.distanceTo(target);

    if (this.avatarHasMoved) {
      const tween = new TWEEN.Tween(this.root.parent.position)
        .to(
          {
            x: target.x,
            y: target.y - 1.25,
            z: target.z,
          },
          distance / (AVATAR_WALKING_SPEED / 1000),
        )
        .onStart(() => {
          this.fadeInAnimation('walking');
          if (onStart) onStart(tween);
        })
        .onComplete(() => {
          this.fadeInAnimation('idle');
          if (onComplete) onComplete(tween);
        })
        .onUpdate(() => {
          this.root.quaternion.set(qt.x, qt.y, qt.z, qt.w);
        })
        .start();
    } else {
      this.root.parent.position.set(target.x, target.y - 1.25, target.z);
      this.root.visible = true;
      this.avatarHasMoved = true;
    }
  }

  rotateTo(target: Quaternion, onStart, onComplete) {
    const alpha = { current: 0 };
    const tween = new TWEEN.Tween(alpha)
      .to({ current: 1 }, 1000 * AVATAR_ROTATION_SPEED)
      .onStart(() => {
        if (onStart) onStart(tween);
      })
      .onUpdate(() => {
        this.root.quaternion.slerpQuaternions(this.root.quaternion, target, alpha.current);
      })
      .onComplete(() => {
        if (onComplete) onComplete(tween);
      })
      .start();
  }

  onDestroy() {
    if (this.label) {
      this.label.geometry.dispose();
      this.label.material.dispose();
    }
  }

  onTick(tickDelta: number) {
    if (this.context.camera && this.inputs.bodyType === 'image') {
      this.context.camera.getWorldPosition(this.vec3);
      const { x, z } = this.vec3;

      const vec = new this.context.three.Vector3();

      this.root.getWorldPosition(vec);
      this.root.lookAt(x, vec.y, z);
    }

    if (this.mixer) {
      this.mixer.update(tickDelta / 1000);
    }
    // super.onTick(tickDelta);
  }

  // called back when any inputs properties change
  onInputsUpdated() {
    if (this.outputs.loadingState !== LoadingState.Loading) this.onInit();
  }

  private setMorphTarget = (mesh: any | undefined, key: string, value: any) => {
    if (!mesh) return;
    const idx = mesh.morphTargetDictionary[key];
    if (!idx) return;
    mesh.morphTargetInfluences[idx] = value;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,class-methods-use-this
  private updateHeadRotation(pitch: number, yaw: number, roll: number) {
    // Inverse yaw & roll effects for yourself to give mirror effect
    this.pivot.rotation.set(pitch, yaw, roll);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,class-methods-use-this
  private updatePosition(x: number, y: number, z: number) {
    // const SCALE = 0.2;
    // const Y_OFFSET = 0;
    // this.avatarRoot?.position.set(x * SCALE, Y_OFFSET + y * SCALE, z);
  }
}

export const avatarComponentType = 'mp.avatarComponent';
export const makeAvatarComponent = () => new AvatarComponent();
